import Business from "@mui/icons-material/Business";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  AssociateFacilitiesDialog,
  FacilityRow,
} from "components/AssociateFacilities/AssociateFacilitiesDialog";
import { Dialog } from "components/Dialog";
import {
  getStepperSubmissionCombo,
  getWasteSubmissionFormName,
  isStepperEligibleSubmission,
} from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionMethod, SubmissionType } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useState } from "react";

import { isSubmissionMethod, isSubmissionType } from "./submissionEnumGuards";

export type CreateStepperArgs = {
  submissionType: SubmissionType;
  submissionMethod: SubmissionMethod;
  facilityId: string;
  title: string;
  dueDate: DateTime;
};

type SubmissionFlow = "stepper" | "paperForm" | "documentReview";

// The 8700-12 combo from the shared single source of truth — the dialog never
// hardcodes a (type, method) pair that could drift from it.
const STEPPER_COMBO = getStepperSubmissionCombo();

// Display name derived from the same shared source of truth; the fallback only
// applies if the stepper combo ever stops resolving to a known form.
const STEPPER_FORM_NAME =
  getWasteSubmissionFormName(STEPPER_COMBO.type, STEPPER_COMBO.method) ?? "EPA Form 8700-12";

const SUBMISSION_TYPE_OPTIONS = Object.values(SubmissionType).sort((a, b) =>
  prettyPrintEnumValue(a).localeCompare(prettyPrintEnumValue(b))
);
const SUBMISSION_METHOD_OPTIONS = Object.values(SubmissionMethod).sort((a, b) =>
  prettyPrintEnumValue(a).localeCompare(prettyPrintEnumValue(b))
);

/** Single-line address summary for a selected facility, e.g. "123 Main St, Austin, TX 78701". */
function facilityAddress(facility: FacilityRow): string {
  const { streetAddress1, city, state, zip } = facility;
  return [streetAddress1, city, state, zip].filter(Boolean).join(", ");
}

/**
 * The submission title is derived from the facility and the form (it is no
 * longer user-chosen), mirroring how a Tier II report's identity comes from its
 * report kind rather than a free-text name.
 */
function deriveTitle(facility: FacilityRow, type: string, method: string): string {
  const formName = getWasteSubmissionFormName(type, method) ?? prettyPrintEnumValue(type);
  return `${facility.name}: ${formName}`;
}

/**
 * Flow picker shown from "Create Submission" when either guided submission flow
 * is enabled. Instead of asking for the (type, method) combo up front, it
 * presents the flows directly:
 *
 * - "EPA Form 8700-12" (interactive-stepper flag) collects facility and due
 *   date; the combo comes from the shared source of truth and the user enters
 *   the form section by section in Encamp.
 * - "Document submission (guided)" (document-stepper flag) collects the
 *   type/method combo plus facility and due date; the filled-out form is
 *   attached as a document in the guided flow. Interactive combos are blocked —
 *   they are never document submissions.
 * - "All other submission types" routes to the document-review create form,
 *   where the type, method, and remaining fields are entered as before.
 *
 * When both flags are off, the caller skips this dialog entirely.
 */
export function CreateSubmissionTypeDialog({
  open,
  onClose,
  onContinueLegacy,
  onCreateStepper,
  creating = false,
  showStepperOption = true,
  showPaperFormOption = false,
}: {
  open: boolean;
  onClose: () => void;
  onContinueLegacy: () => void;
  onCreateStepper: (args: CreateStepperArgs) => void;
  creating?: boolean;
  showStepperOption?: boolean;
  showPaperFormOption?: boolean;
}) {
  const theme = useTheme();
  const [flow, setFlow] = useState<SubmissionFlow | "">("");
  const [paperType, setPaperType] = useState<SubmissionType | "">("");
  const [paperMethod, setPaperMethod] = useState<SubmissionMethod | "">("");
  const [facility, setFacility] = useState<FacilityRow | null>(null);
  const [dueDate, setDueDate] = useState<DateTime | null>(null);
  const [facilityDialogOpen, setFacilityDialogOpen] = useState(false);

  // Interactive combos are entered field-by-field, never attached as a paper
  // form — the registry predicate blocks every one of them, not just today's.
  const paperComboIsInteractive =
    !!paperType && !!paperMethod && isStepperEligibleSubmission(paperType, paperMethod);
  const canCreateStepper = !!facility && !!dueDate;
  const canCreatePaperForm =
    canCreateStepper && !!paperType && !!paperMethod && !paperComboIsInteractive;

  const reset = () => {
    setFlow("");
    setPaperType("");
    setPaperMethod("");
    setFacility(null);
    setDueDate(null);
    setFacilityDialogOpen(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleCreateStepper = () => {
    if (!facility || !dueDate) {
      return;
    }
    // The guards always pass — the Prisma and GraphQL enums share their string
    // values — but narrowing this way avoids an `as` cast.
    if (!isSubmissionType(STEPPER_COMBO.type) || !isSubmissionMethod(STEPPER_COMBO.method)) {
      return;
    }
    onCreateStepper({
      submissionType: STEPPER_COMBO.type,
      submissionMethod: STEPPER_COMBO.method,
      facilityId: facility.id,
      title: deriveTitle(facility, STEPPER_COMBO.type, STEPPER_COMBO.method),
      dueDate,
    });
  };

  const handleCreatePaperForm = () => {
    if (!facility || !dueDate || !paperType || !paperMethod || paperComboIsInteractive) {
      return;
    }
    onCreateStepper({
      submissionType: paperType,
      submissionMethod: paperMethod,
      facilityId: facility.id,
      title: deriveTitle(facility, paperType, paperMethod),
      dueDate,
    });
  };

  // The primary action adapts to the chosen flow. All follow the standard
  // dialog action row (Cancel on the left, primary on the right).
  let primaryAction = (
    <Button variant="contained" disabled={flow === ""} onClick={onContinueLegacy}>
      Next
    </Button>
  );
  if (flow === "stepper") {
    primaryAction = (
      <Button
        variant="contained"
        loading={creating}
        disabled={!canCreateStepper}
        onClick={handleCreateStepper}
      >
        Continue
      </Button>
    );
  } else if (flow === "paperForm") {
    primaryAction = (
      <Button
        variant="contained"
        loading={creating}
        disabled={!canCreatePaperForm}
        onClick={handleCreatePaperForm}
      >
        Continue
      </Button>
    );
  }

  const showGuidedFields = flow === "stepper" || flow === "paperForm";

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      title="Create Submission"
      description="Select which type of waste submission to create"
      primaryActionButton={primaryAction}
    >
      <RadioGroup
        aria-label="Submission flow"
        value={flow}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "stepper" || value === "paperForm" || value === "documentReview") {
            setFlow(value);
          }
        }}
      >
        {showStepperOption && (
          <FormControlLabel
            value="stepper"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0 } }}
            label={
              <Stack>
                <Typography variant="body1">{STEPPER_FORM_NAME}</Typography>
                <Typography variant="caption" color="text.secondary">
                  Fill out the RCRAInfo myRCRAid form section by section in Encamp.
                </Typography>
              </Stack>
            }
          />
        )}
        {showPaperFormOption && (
          <FormControlLabel
            value="paperForm"
            control={<Radio />}
            sx={{ alignItems: "flex-start", mb: 1, "& .MuiRadio-root": { pt: 0 } }}
            label={
              <Stack>
                <Typography variant="body1">Document submission (guided)</Typography>
                <Typography variant="caption" color="text.secondary">
                  Attach the filled-out form and track the filing in a guided flow.
                </Typography>
              </Stack>
            }
          />
        )}
        <FormControlLabel
          value="documentReview"
          control={<Radio />}
          sx={{ alignItems: "flex-start", "& .MuiRadio-root": { pt: 0 } }}
          label={
            <Stack>
              <Typography variant="body1">All other submission types</Typography>
              <Typography variant="caption" color="text.secondary">
                Choose the submission type and method, then upload the filing for document review.
              </Typography>
            </Stack>
          }
        />
      </RadioGroup>

      {flow === "paperForm" && (
        <>
          <FormControl fullWidth>
            <InputLabel id="paper-form-type-label">Submission type *</InputLabel>
            <Select
              labelId="paper-form-type-label"
              label="Submission type *"
              value={paperType}
              onChange={(e) => {
                if (isSubmissionType(e.target.value)) {
                  setPaperType(e.target.value);
                }
              }}
            >
              {SUBMISSION_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>
                  {prettyPrintEnumValue(type)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="paper-form-method-label">Submission method *</InputLabel>
            <Select
              labelId="paper-form-method-label"
              label="Submission method *"
              value={paperMethod}
              onChange={(e) => {
                if (isSubmissionMethod(e.target.value)) {
                  setPaperMethod(e.target.value);
                }
              }}
            >
              {SUBMISSION_METHOD_OPTIONS.map((method) => (
                <MenuItem key={method} value={method}>
                  {prettyPrintEnumValue(method)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {paperComboIsInteractive && (
            <Typography variant="caption" color="error">
              This type and method combination is not available as a document submission.
            </Typography>
          )}
        </>
      )}

      {showGuidedFields && (
        <>
          {/* Facility selector mirrors the document-review picker (omnisearch by
              name, address, county, tags, …) — name-only search isn't enough for
              larger tenants. Renders the selected facility with a "Change"
              affordance, matching the document-review field. */}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Facility *
            </Typography>
            {facility ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  p: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight="medium" noWrap>
                    {facility.name}
                  </Typography>
                  {facilityAddress(facility) && (
                    <Typography variant="caption" color="text.secondary" noWrap component="div">
                      {facilityAddress(facility)}
                    </Typography>
                  )}
                </Box>
                <Button size="small" onClick={() => setFacilityDialogOpen(true)}>
                  Change
                </Button>
              </Box>
            ) : (
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<Business />}
                  onClick={() => setFacilityDialogOpen(true)}
                >
                  Select facility
                </Button>
              </Box>
            )}
          </Box>

          <DatePicker
            label="Due date *"
            value={dueDate}
            onChange={setDueDate}
            slotProps={{ textField: { fullWidth: true } }}
          />

          <AssociateFacilitiesDialog
            open={facilityDialogOpen}
            onClose={() => setFacilityDialogOpen(false)}
            singleSelect
            existingFacilityIds={facility ? [facility.id] : []}
            // "Select" is disabled with nothing selected, so a null here is
            // always the off-page-row artifact (the chosen facility sorts past
            // the grid's first page and never populated `selectedRows`), never a
            // real clear — ignore it so we don't wipe a valid selection.
            onSaveSingle={(selected) => {
              if (selected) setFacility(selected);
            }}
            onSave={() => undefined}
            subtitle="Select the facility for this submission."
            excludeSecondaryWasteFacilities
          />
        </>
      )}
    </Dialog>
  );
}
